import { first } from 'rxjs/operators';
import { useCitationsApiContext, useModalApiContext } from '@vivli/core/infrastructure/context';
import { IRorResponseItem } from '@vivli/features/organizations/infrastructure/interface';
import { ICheckboxMenuItem } from '@vivli/shared/infrastructure/interface';
import { useRorHelpers } from './use-ror-helpers.hook';

const promptText =
    'Please select the best-matching ROR organization. If no ROR organizations appear, please cancel and enter a different name.';

const getCheckboxes = (matches: IRorResponseItem[]) => {
    const { getRorName, getRorWebsite } = useRorHelpers();
    const checkboxValues: ICheckboxMenuItem[] = [];
    matches.map((r) => {
        const name = getRorName(r);
        const site = getRorWebsite(r);
        let title = `${name}: (${site})${' '.repeat(8)}Location: ${r.locations[0].geonames_details.name}`;
        const parent = r.relationships.find((r) => r.type.toLowerCase() === 'parent');
        if (parent) {
            title = title.concat(`    Parent: ${parent.label}`);
        }
        checkboxValues.push({
            title: title,
            value: r.id,
            checked: false,
        });
    });
    return checkboxValues;
};

interface ISearchOptions {
    searchTerm: string;
    onConfirm: (selection: string, matches: IRorResponseItem[]) => void;
    onError: (err, message: string) => void;
    setIsLoading: (value: boolean) => void;
}

export const useRorSearchHook = () => {
    const citationsService = useCitationsApiContext();
    const modalService = useModalApiContext();

    return ({ searchTerm, onConfirm, onError, setIsLoading }: ISearchOptions) =>
        citationsService
            .getRorMatches(searchTerm)
            .pipe(first())
            .subscribe(
                (matches) => {
                    return modalService.confirm(promptText, {
                        title: 'Find ROR Organization',
                        style: { textAlign: 'left', minWidth: '50em' },
                        messageStyle: { textAlign: 'left' },
                        multiSelectOptions: getCheckboxes(matches),
                        showMultiSelect: true,
                        multiSelectAllowSingleOnly: true,
                        requireSelectOption: true,
                        showTextAreaPrompt: false,
                        showPrompt: false,
                        confirmText: 'OK',
                        cancelText: 'Cancel',
                        onConfirm: ({ selections }) => {
                            onConfirm(selections[0], matches);
                        },
                        onCancel: () => setIsLoading(false),
                    });
                },
                (e) => {
                    onError(e, 'There was a problem getting ROR matches: ');
                }
            );
};
